import React from "react";
import {
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  AssignmentTurnedInRounded,
  FilterListRounded,
} from "@mui/icons-material";
import DatesItem from "./DatesItem";
import {
  EmptyState,
  PageIntro,
  PageShell,
  SectionBlock,
  StatStrip,
} from "../ui/AppPrimitives";
import { startOfDay } from "../utils/dateUtils";

const filterSections = [
  {
    title: "View",
    options: [
      { value: "all", label: "All Dates" },
      { value: "important", label: "Important Only" },
    ],
  },
  {
    title: "Time",
    options: [
      { value: "today", label: "Today" },
      { value: "week", label: "This Week" },
      { value: "month", label: "This Month" },
    ],
  },
  {
    title: "Category",
    options: [
      { value: "personal", label: "Personal" },
      { value: "legal", label: "Legal" },
      { value: "health", label: "Health" },
      { value: "exam", label: "Exam" },
      { value: "other", label: "Other" },
    ],
  },
];

const CompletedDates = ({ dates, onDelete, onPriority }) => {
  const [filter, setFilter] = React.useState("all");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isOpen = Boolean(anchorEl);

  const grouped = React.useMemo(() => {
    const today = startOfDay(new Date());
    const startWeek = new Date(today);
    startWeek.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startYear = new Date(today.getFullYear(), 0, 1);

    const result = {
      today: [],
      week: [],
      month: [],
      year: [],
      later: [],
    };

    const applyFilter = (item) => {
      if (!item.dateTime || item.type === "recurring") return false;
      const eventDate = startOfDay(item.dateTime);
      const isCompleted = eventDate < today;
      if (!isCompleted) return false;

      switch (filter) {
        case "important":
          return item.priority;
        case "today":
          return eventDate.getTime() === today.getTime();
        case "week":
          return eventDate < today && eventDate >= startWeek;
        case "month":
          return eventDate < startWeek && eventDate >= startMonth;
        case "personal":
        case "legal":
        case "health":
        case "exam":
        case "other":
          return item.category?.toLowerCase() === filter;
        default:
          return true;
      }
    };

    dates.filter(applyFilter).forEach((item) => {
      const itemDate = startOfDay(item.dateTime);

      if (itemDate.getTime() === today.getTime()) {
        result.today.push(item);
      } else if (itemDate >= startWeek) {
        result.week.push(item);
      } else if (itemDate >= startMonth) {
        result.month.push(item);
      } else if (itemDate >= startYear) {
        result.year.push(item);
      } else {
        result.later.push(item);
      }
    });

    Object.keys(result).forEach((key) => {
      result[key].sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
    });

    return result;
  }, [dates, filter]);

  const completedDates = Object.values(grouped).flat();
  const priorityCount = completedDates.filter((item) => item.priority).length;

  const filterLabel =
    filterSections
      .flatMap((section) => section.options)
      .find((option) => option.value === filter)?.label || "All Dates";

  const renderGroup = (label, items) => {
    if (items.length === 0) return null;

    return (
      <SectionBlock
        key={label}
        title={label}
        subtitle={`${items.length} event${items.length > 1 ? "s" : ""}`}
      >
        <Stack spacing={1.6}>
          {items.map((item) => (
            <DatesItem
              key={item.id}
              dateItem={item}
              onDelete={onDelete}
              onPriority={onPriority}
            />
          ))}
        </Stack>
      </SectionBlock>
    );
  };

  return (
    <PageShell maxWidth="xl">
      <PageIntro
        eyebrow="Completed"
        title="A clean archive of what you’ve already crossed."
        subtitle="Review finished dates without cluttering your upcoming timeline. Completed events stay available for context and cleanup."
        action={
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FilterListRounded />}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {filterLabel}
          </Button>
        }
      />

      <StatStrip
        items={[
          {
            label: "Archived dates",
            value: completedDates.length,
            caption: "Past one-time events",
          },
          {
            label: "Priority",
            value: priorityCount,
            caption: "Marked as important",
            tint: "#f59e0b",
          },
        ]}
      />

      {completedDates.length === 0 ? (
        <EmptyState
          icon={<AssignmentTurnedInRounded color="primary" sx={{ fontSize: 40 }} />}
          title="No completed dates yet"
          description="As events pass, they’ll move here automatically so your active dashboard stays focused."
        />
      ) : (
        <Box>
          {renderGroup("Today", grouped.today)}
          {renderGroup("This Week", grouped.week)}
          {renderGroup("This Month", grouped.month)}
          {renderGroup("This Year", grouped.year)}
          {renderGroup("Later", grouped.later)}
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 260,
            borderRadius: 4,
            mt: 1,
          },
        }}
      >
        {filterSections.map((section) => (
          <Box key={section.title} sx={{ py: 0.4 }}>
            <Typography
              variant="overline"
              sx={{ px: 2, color: "text.secondary", display: "block" }}
            >
              {section.title}
            </Typography>
            {section.options.map((option) => (
              <MenuItem
                key={option.value}
                selected={filter === option.value}
                onClick={() => {
                  setFilter(option.value);
                  setAnchorEl(null);
                }}
              >
                <Stack direction="row" justifyContent="space-between" sx={{ width: "100%" }}>
                  <span>{option.label}</span>
                  {filter === option.value && (
                    <Chip label="Active" size="small" color="primary" />
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Box>
        ))}
      </Menu>
    </PageShell>
  );
};

export default CompletedDates;
